/* eslint-disable no-unused-expressions */
import { Typography } from '@mui/material';
import { useEffect, useState, ChangeEvent } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import moment from 'moment';
import axios from 'axios';
import { object, ZodError, string } from 'zod';
import AdminManagementComponent from '../../components/Admins/AdminManagement';
import { ColumnSpec } from '../../components/common/DataTable/types';
import UserAvatar from '../../components/common/UserAvatar/UserAvatar';
import { User } from '../../components/Dashboard/types';
import getLocalStorageData, { setLocalStorageData } from '../../utils/token';
import styles from './styles';
import getAPIUrl from '../../config';
import { getApi, putApiWithAuth } from '../../utils/apiCalls';
import MyProfileDetails from '../../components/MyProfile/ProfileDetails';
import { IAdmin } from '../../components/Admins/AdminManagement/types';
import EditMyProfile from '../../components/MyProfile/EditProfile';
import { UserData } from '../../components/MyProfile/ProfileDetails/types';
import AdminProfileDetails from '../../components/Admins/AdminDetails';
import EditAdminProfile from '../../components/Admins/EditAdminDetails';
import getAdmins from './Api';
import { SortDirection } from '../../components/common/DataTable/sortable-header';
import editProfileSchema from '../../utils/schemas';
import { useToast } from '../../components/common/ToasterContext';
import { showLoaderAfterTimeout, trimFormData } from '../../utils/helpers';

const AdminManagement = () => {
    const userData = getLocalStorageData();
    const [adminsData, setAdminsData] = useState<IAdmin[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [adminsCount, setAdminsCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [openProfileDetails, setOpenProfileDetails] = useState(false);
    const size = 10;
    const [editProfileDetails, setEditProfileDetails] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [userInfo, setUserInfo] = useState<UserData>({
        ...userData,
        password: '',
        repeatPassword: '',
        roleId: '',
    });

    const [roles, setRoles] = useState<Array<{ name: string; value: number }>>([]);
    const [createAdmin, setCreateAdmin] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedRole, setSelectedRole] = useState<number>(-1);
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [openAdminDetails, setOpenAdminDetails] = useState(false);
    const [selectedAdminImage, setSelectedAdminImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedAdminFile, setSelectedAdminFile] = useState<File>();
    const [filters, setFilters] = useState<boolean>();
    const [selectedImage, setSelectedImage] = useState<string | ArrayBuffer | null | undefined>();
    const [selectedFile, setSelectedFile] = useState<File>();
    const [selectedUserData, setSelectedUserData] = useState<IAdmin>({
        createdAt: '',
        email: '',
        firstname: '',
        id: '',
        isActive: false,
        lastname: '',
        profileImageUrl: '',
        signedImageUrl: '',
        roleId: '',
    });
    const [userActive, setUserActive] = useState<boolean>(false);
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const [adminFormErrors, setAdminFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const user: User = {
        name: `${userData.firstname} ${userData.lastname}`,
        profileImg: userData.signedImageUrl,
    };
    const [reload, setReload] = useState<boolean>(false);
    const [editAdminDetails, setEditAdminDetails] = useState(false);
    const [loader, setLoader] = useState(true);

    const [sortKey, setSortkey] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [submitting, setSubmitting] = useState<boolean>(false);

    const toast = useToast();

    const editAdminProfileSchema = object({
        firstname: string().nonempty({ message: 'Please enter a first name.' }),
        lastname: string().nonempty({ message: 'Please enter a last name.' }),
        roleId: string().nonempty({ message: 'Please select a role.' }),
        email: string()
            .nonempty({ message: 'Please provide an email.' })
            .email({ message: 'Please provide a valid email address' }),
    });

    const onStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedStatus(event.target.value);
    };
    const onRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedRole(Number(event.target.value));
    };
    const onPeriodChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedPeriod(event.target.value);
    };

    const onFiltersClearClick = async () => {
        setSelectedRole(-1);
        setSelectedStatus('');
        setSelectedPeriod('');
        setCurrentPage(1);
        setFilters(!filters);
    };

    const onFilterChipDelete = async (type: 'status' | 'role' | 'additionPeriod') => {
        if (type === 'status') {
            setSelectedStatus('');
        } else if (type === 'role') {
            setSelectedRole(-1);
        } else {
            setSelectedPeriod('');
        }
        setCurrentPage(1);
        setFilters(!filters);
    };

    const onFiltersApplyClick = async () => {
        setAdminsData([]);
        setCurrentPage(1);
        setFilters(!filters);
    };

    const onSearchChange = async (text: string) => {
        setSearchText(text);
        setCurrentPage(1);
    };

    const columnSpec: ColumnSpec<IAdmin>[] = [
        {
            name: 'name',
            title: 'Admin',
            cellProperty: 'name',
            cellRenderer: (admin: IAdmin) => (
                <Typography
                    sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }}
                    component="div"
                >
                    <UserAvatar
                        id="admin_profile_pic"
                        name={`${admin.firstname} ${admin.lastname}`}
                        size={40}
                        source={admin.signedUrl || ''}
                        radius="20px"
                    />
                    <Typography component="span" sx={styles.title}>
                        {admin.firstname} {admin.lastname}
                    </Typography>
                </Typography>
            ),
        },
        {
            name: 'roleId',
            title: 'Role',
            cellProperty: 'roleId',
            cellRenderer: (admin: IAdmin) => <>{admin.adminRole?.role}</>,
        },
        {
            name: 'email',
            title: 'Email',
            cellProperty: 'email',
            cellRenderer: (admin: IAdmin) => (
                <CopyToClipboard text={admin.email}>
                    <Typography sx={styles.email}>
                        <img src="/images/copy-to-clipboard.png" alt="Copy To clip board" />
                        <Typography component="span" sx={styles.rowLabel}>
                            {admin.email}
                        </Typography>
                    </Typography>
                </CopyToClipboard>
            ),
        },
        {
            name: 'createdAt',
            title: 'Added',
            cellProperty: 'createdAt',
            cellRenderer: (admin: IAdmin) => <>{moment(new Date(admin.createdAt)).format('MMM DD, YYYY')}</>,
        },
        {
            name: 'status',
            title: 'Status',
            cellProperty: 'isActive',
            cellRenderer: (admin: IAdmin) => (
                <img alt="status" src={admin.isActive ? '/images/active-toggle.png' : '/images/disable-toggle.png'} />
            ),
        },
    ];

    useEffect(() => {
        setLoader(true);
        getAdmins({
            size,
            currentPage,
            searchText,
            selectedRole,
            selectedPeriod,
            selectedStatus,
            sort: sortKey,
            order: sortOrder,
        })
            .then((res) => {
                const admins = res.data.data;
                setAdminsData(admins.data);
                setAdminsCount(admins.count);
                showLoaderAfterTimeout(setLoader);
            })
            .catch((_error) => {
                showLoaderAfterTimeout(setLoader);
            });
    }, [filters, searchText]);

    useEffect(() => {
        getApi(`${getAPIUrl()}/admins/admin-roles`).then((res) => {
            const rolesData = res.data.data.data.map((r: { role: string; id: string }) => ({
                name: r.role,
                value: Number(r.id),
            }));
            setRoles(rolesData);
            const roleData = rolesData.find((role: { name: string; value: number }) => role.name === userData.roleName);
            setSelectedRoleId(String(roleData?.value));
            setUserInfo({ ...userInfo, roleId: String(roleData?.value) });
        });
    }, []);

    const loadMore = () => {
        const loadItems = async () => {
            if (size * (currentPage - 1) < adminsCount) {
                const res = await getAdmins({
                    size,
                    currentPage: currentPage + 1,
                    searchText,
                    selectedRole,
                    selectedPeriod,
                    selectedStatus,
                    sort: sortKey,
                    order: sortOrder,
                });
                setCurrentPage(currentPage + 1);
                if (!axios.isAxiosError(res)) {
                    const aData = [...adminsData, ...res.data.data.data];
                    if (!(aData.length === adminsCount)) {
                        setAdminsCount(res.data.data.count);
                    }
                    setAdminsData(aData);
                    showLoaderAfterTimeout(setLoader);
                }
            }
        };
        setHasMore(!(adminsData.length === adminsCount));
        loadItems();
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            editProfileSchema.parse(trimFormData(userInfo));
            const formData = new FormData();
            formData.append('roleId', selectedRoleId);
            formData.append('firstname', userInfo.firstname);
            formData.append('lastname', userInfo.lastname);
            formData.append('imageFile', selectedFile as any);
            if (userInfo.password) {
                formData.append('password', String(userInfo.password || ''));
            }
            if (userInfo.repeatPassword) {
                formData.append('repeatPassword', String(userInfo.repeatPassword || ''));
            }
            const result = await putApiWithAuth(`${getAPIUrl()}/admins/edit-admin`, formData);
            setFormErrors(undefined);
            setUserInfo({
                ...userInfo,
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
                roleName: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                password: '',
                repeatPassword: '',
            });
            setLocalStorageData({
                ...userData,
                adminRole: {
                    role: roles.find((r) => r.value === Number(result.data.data.data.roleId))?.name || '',
                },
                email: result.data.data.data.email,
                firstname: result.data.data.data.firstname,
                lastname: result.data.data.data.lastname,
                profileImageUrl: result.data.data.data.profileImageUrl,
                signedImageUrl: result.data.data.data.signedImageUrl,
            });
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }
            setSelectedRoleId(result.data.data.data.roleId);
            setEditProfileDetails(false);
            setOpenProfileDetails(false);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const reloadData = async (load: boolean) => {
        setReload(load);
        const res = await getAdmins({
            size,
            currentPage: 1,
            searchText,
            selectedRole,
            selectedPeriod,
            selectedStatus,
            sort: sortKey,
            order: sortOrder,
        });
        if (!axios.isAxiosError(res)) {
            setAdminsData(res.data.data.data);
            setAdminsCount(res.data.data.count);
            showLoaderAfterTimeout(setLoader);
        }
    };

    const saveAdminChanges = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            editAdminProfileSchema.parse(trimFormData(selectedUserData));
            const formData = new FormData();
            formData.append('id', selectedUserData.id);
            formData.append('roleId', selectedRoleId);
            formData.append('firstname', selectedUserData.firstname);
            formData.append('lastname', selectedUserData.lastname);
            formData.append('imageFile', selectedAdminFile as any);

            const result = await putApiWithAuth(`${getAPIUrl()}/admins/other-admin`, formData);
            setSelectedUserData({
                ...selectedUserData,
                id: selectedUserData.id,
                email: result.data.data.email,
                firstname: result.data.data.firstname,
                lastname: result.data.data.lastname,
                profileImageUrl: result.data.data.profileImageUrl,
                roleId: result.data.data.roleId,
                signedImageUrl: result.data.data.signedImageUrl,
            });
            if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage('Request Processeed');
                toast.onSuccess();
            }

            setOpenAdminDetails(false);
            setEditAdminDetails(false);
            await reloadData(!reload);
        } catch (error: any) {
            if (error instanceof ZodError) {
                setAdminFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                toast.onToggleSnackbar();
                toast.onMessage(error.response.data.errors);
                toast.onError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(value);
        }
        setUserInfo({
            ...userInfo,
            [name]: String(value),
        });
    };

    const handleEditAdminValues = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.target;
        if (name === 'roleId') {
            setSelectedRoleId(value);
        }
        setSelectedUserData({
            ...selectedUserData,
            [name]: String(value),
        });
    };

    const handleRowClick = (rowData: IAdmin) => {
        setSelectedUserData(rowData);
        setSelectedRoleId(rowData.roleId);
        setUserActive(rowData.isActive);
        setOpenAdminDetails(true);
    };

    const handleChangeAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleChangeAdminAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedAdminFile(e.target.files[0]);
            const reader = new FileReader();
            reader.onload = (event) => {
                setSelectedAdminImage(event?.target?.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const sortData = async (key: string, direction: SortDirection) => {
        const res = await getAdmins({
            size,
            sort: key,
            order: direction === 0 ? 'desc' : 'asc',
            currentPage: 1,
            searchText,
            selectedRole,
            selectedPeriod,
            selectedStatus,
        });
        if (!axios.isAxiosError(res)) {
            setAdminsData(res.data.data.data);
            setAdminsCount(res.data.data.count);
            setCurrentPage(1);
            setSortkey(key);
            setSortOrder(direction === 0 ? 'desc' : 'asc');
            setHasMore(true);
            showLoaderAfterTimeout(setLoader);
        }
    };

    const handleCloseMyProfile = (value: boolean) => {
        setUserInfo({
            ...userData,
            password: '',
            repeatPassword: '',
            roleId: String(
                roles.find((role: { name: string; value: number }) => role.name === userData.roleName)?.value,
            ),
        });
        setEditProfileDetails(value);
        setFormErrors(undefined);
    };

    return (
        <>
            <AdminManagementComponent
                columns={columnSpec}
                tableData={adminsData}
                user={user}
                loadMore={loadMore}
                hasMore={hasMore}
                roles={roles}
                searchChange={onSearchChange}
                selectedRole={selectedRole}
                selectedStatus={selectedStatus}
                selectedPeriod={selectedPeriod}
                onStatusChange={onStatusChange}
                onRoleChange={onRoleChange}
                onPeriodChange={onPeriodChange}
                onFlitersClearClick={onFiltersClearClick}
                onFilterChipDelete={onFilterChipDelete}
                onFlitersApplyClick={onFiltersApplyClick}
                createAdmin={createAdmin}
                setCreateAdmin={setCreateAdmin}
                openProfileDetails={openProfileDetails}
                setOpenProfileDetails={setOpenProfileDetails}
                handleRowClick={handleRowClick}
                setReload={reloadData}
                adminsCount={adminsCount}
                loader={loader}
                sortData={sortData}
            />

            {openProfileDetails ? (
                <MyProfileDetails
                    openProfileDetails={openProfileDetails}
                    setOpenProfileDetails={setOpenProfileDetails}
                    setEditProfileDetails={setEditProfileDetails}
                    userData={userData}
                />
            ) : null}
            {editProfileDetails ? (
                <EditMyProfile
                    editProfileDetails={editProfileDetails}
                    userInfo={userInfo}
                    handleInput={handleInput}
                    handleSubmit={onSubmit}
                    roles={roles}
                    selectedRoleId={selectedRoleId}
                    handleChangeAvatar={handleChangeAvatar}
                    selectedImage={selectedImage}
                    formErrors={formErrors}
                    handleCloseMyProfile={handleCloseMyProfile}
                    submitting={submitting}
                />
            ) : null}
            {editAdminDetails ? (
                <EditAdminProfile
                    editAdminDetails={editAdminDetails}
                    setEditAdminDetails={setEditAdminDetails}
                    adminInfo={selectedUserData}
                    handleInput={handleEditAdminValues}
                    handleSubmit={saveAdminChanges}
                    roles={roles}
                    selectedRoleId={selectedRoleId}
                    handleChangeAvatar={handleChangeAdminAvatar}
                    selectedImage={selectedAdminImage}
                    formErrors={adminFormErrors}
                    submitting={submitting}
                />
            ) : null}
            {openAdminDetails ? (
                <AdminProfileDetails
                    openAdminDetails={openAdminDetails}
                    setOpenAdminDetails={setOpenAdminDetails}
                    userData={selectedUserData}
                    setEditAdminDetails={setEditAdminDetails}
                    userActive={userActive}
                    setUserActive={setUserActive}
                    setReload={reloadData}
                />
            ) : null}
        </>
    );
};
export default AdminManagement;
